/**
 * MusicSheet Converter & Utilities
 * Provides bidirectional parsing & conversion between internal Note models,
 * ABC notation, and standard MusicXML.
 */

// Diatonic steps in an octave
export const STEP_NAMES = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

// MIDI base values for octave 4
export const NOTE_TO_SEMITONE = {
    'C': 0, 'D': 2, 'E': 4, 'F': 5, 'G': 7, 'A': 9, 'B': 11
};

/**
 * Calculates diatonic index (C4 = 0, D4 = 1, ..., B4 = 6, C5 = 7, etc.)
 */
export function getDiatonicIndex(step, octave) {
    const stepIdx = STEP_NAMES.indexOf(step.toUpperCase());
    if (stepIdx === -1) return 0;
    return (octave - 4) * 7 + stepIdx;
}

/**
 * Gets step and octave from diatonic index (0 -> C4, 1 -> D4, etc.)
 */
export function getPitchFromDiatonicIndex(index) {
    const totalSteps = index;
    const stepIdx = ((totalSteps % 7) + 7) % 7;
    const octave = 4 + Math.floor(totalSteps / 7);
    return {
        step: STEP_NAMES[stepIdx],
        octave: octave,
        pitch: `${STEP_NAMES[stepIdx]}${octave}`
    };
}

/**
 * Calculates MIDI note number
 */
export function getMidiNumber(step, octave, accidental = '') {
    const base = 12 + (octave * 12) + (NOTE_TO_SEMITONE[step.toUpperCase()] || 0);
    let alt = 0;
    if (accidental === '##' || accidental === '^^') alt = 2;
    else if (accidental === '#' || accidental === '^') alt = 1;
    else if (accidental === 'bb' || accidental === '__') alt = -2;
    else if (accidental === 'b' || accidental === '_') alt = -1;
    return base + alt;
}

/**
 * Calculates frequency in Hz for a note
 */
export function getNoteFrequency(step, octave, accidental = '') {
    const midi = getMidiNumber(step, octave, accidental);
    return 440 * Math.pow(2, (midi - 69) / 12);
}

/**
 * ABC pitch token for a single pitch, e.g. ^C, _B,, c'
 */
export function abcPitchToken(step, octave, accidental = '', cautionary = false) {
    let acc = '';
    if (accidental === '##' || accidental === '^^') acc = cautionary ? '(?^^)' : '^^';
    else if (accidental === '#' || accidental === '^') acc = cautionary ? '(?^)' : '^';
    else if (accidental === 'bb' || accidental === '__') acc = cautionary ? '(?__)' : '__';
    else if (accidental === 'b' || accidental === '_') acc = cautionary ? '(?_)' : '_';
    else if (accidental === '=' || accidental === 'n') acc = cautionary ? '(?=)' : '=';

    step = (step || 'C').toUpperCase();
    const oct = octave || 4;
    let letter = '';
    if (oct <= 3) {
        const commas = ','.repeat(4 - oct);
        letter = `${step}${commas}`;
    } else if (oct === 4) {
        letter = step;
    } else if (oct === 5) {
        letter = step.toLowerCase();
    } else {
        const apostrophes = "'".repeat(oct - 5);
        letter = `${step.toLowerCase()}${apostrophes}`;
    }
    return `${acc}${letter}`;
}

/**
 * Key signature name <-> MusicXML fifths (+ mode). Our key vocabulary is small;
 * unknown keys are skipped on import.
 */
export function keyToFifths(key) {
    const map = { C: 0, G: 1, D: 2, F: -1, Bb: -2, Am: 0, Em: 1, Dm: -1, Gm: -2 };
    return map[key] !== undefined ? map[key] : null;
}

export function keyIsMinor(key) {
    return key === 'Am' || key === 'Em' || key === 'Dm' || key === 'Gm';
}

// Alteration implied by the key signature for a step ('' = natural).
// Our key vocabulary only; unknown keys imply nothing.
export function keyAlterFor(step, key) {
    const s = (step || '').toUpperCase();
    const sharps = { G: ['F'], D: ['F', 'C'], Em: ['F'] };
    const flats = { F: ['B'], Bb: ['B', 'E'], Dm: ['B'], Gm: ['B', 'E'] };
    if ((sharps[key] || []).includes(s)) return '#';
    if ((flats[key] || []).includes(s)) return 'b';
    return '';
}

export function keySharps(key) {
    return { G: ['F'], D: ['F', 'C'], Em: ['F'] }[key] || [];
}

export function keyFlats(key) {
    return { F: ['B'], Bb: ['B', 'E'], Dm: ['B'], Gm: ['B', 'E'] }[key] || [];
}

// Chromatic spelling tables (pitch class -> [step, accidental])
const SHARP_SPELL = [['C', ''], ['C', '#'], ['D', ''], ['D', '#'], ['E', ''], ['F', ''], ['F', '#'], ['G', ''], ['G', '#'], ['A', ''], ['A', '#'], ['B', '']];
const FLAT_SPELL = [['C', ''], ['D', 'b'], ['D', ''], ['E', 'b'], ['E', ''], ['F', ''], ['G', 'b'], ['G', ''], ['A', 'b'], ['A', ''], ['B', 'b'], ['B', '']];

// Transpose a MIDI note, spelling with sharps or flats
export function transposePitch(midi, semitones, useFlats = false) {
    const m2 = midi + semitones;
    const pc = ((m2 % 12) + 12) % 12;
    const [step, accidental] = (useFlats ? FLAT_SPELL : SHARP_SPELL)[pc];
    return { step, octave: Math.floor(m2 / 12) - 1, accidental };
}

// Shift a key-signature name by semitones within our vocabulary (null = keep)
export function transposeKey(key, semitones) {
    const minor = keyIsMinor(key);
    const tonicPc = { C: 0, G: 7, D: 2, F: 5, Bb: 10, Am: 9, Em: 4, Dm: 2, Gm: 7 }[key];
    if (tonicPc === undefined) return null;
    const pc = (((tonicPc + semitones) % 12) + 12) % 12;
    if (minor) {
        return { 9: 'Am', 4: 'Em', 2: 'Dm', 7: 'Gm' }[pc] || null;
    }
    return { 0: 'C', 7: 'G', 2: 'D', 5: 'F', 10: 'Bb' }[pc] || null;
}

export function fifthsToKey(fifths, mode) {
    const f = parseInt(fifths, 10);
    if (mode === 'minor') {
        if (f === 0) return 'Am';
        if (f === 1) return 'Em';
        if (f === -1) return 'Dm';
        if (f === -2) return 'Gm';
        return null;
    }
    if (f === 0) return 'C';
    if (f === 1) return 'G';
    if (f === 2) return 'D';
    if (f === -1) return 'F';
    if (f === -2) return 'Bb';
    return null;
}

/**
 * Duration string to beat value (in quarter note beats, where 4 = 1 beat)
 */
export function durationToQuarterBeats(duration, isDotted = false, tupletRatio = 1, dots = 0) {
    let base = 1;
    switch (String(duration)) {
        case '1': base = 4; break;     // Whole note
        case '2': base = 2; break;     // Half note
        case '4': base = 1; break;     // Quarter note
        case '8': base = 0.5; break;   // Eighth note
        case '16': base = 0.25; break; // 16th note
        case '32': base = 0.125; break;// 32nd note (Fusa)
        default: base = 1; break;
    }
    // dots: 0 = none, 1 = single (x1.5), 2 = double (x1.75). Legacy isDotted maps to 1.
    const d = dots || (isDotted ? 1 : 0);
    let val = base;
    if (d >= 1) val += base / 2;
    if (d >= 2) val += base / 4;
    return val * (tupletRatio || 1);
}

export function noteDotCount(note) {
    if (!note) return 0;
    return note.dots || (note.isDotted ? 1 : 0);
}

/**
 * Convert internal note array to ABC Notation string
 */
/**
 * Convert internal notes or voices to ABC Notation string
 */
export function notesToAbc(notesOrVoices, options = {}) {
    const title = options.title || 'Untitled Score';
    const composer = options.composer || '';
    const key = options.key || 'C';
    const meter = options.timeSignature || '4/4';
    const tempo = options.tempo || 120;
    const unitLength = '1/8'; // 1 beat = 2 units of 1/8

    let abc = `X:1\n`;
    abc += `T:${title}\n`;
    if (composer) abc += `C:${composer}\n`;
    abc += `M:${meter}\n`;
    abc += `L:${unitLength}\n`;
    abc += `Q:1/4=${tempo}\n`;

    const isMultiVoice = options.voices && options.voices.length > 1;

    // Parse time signature for barlines
    const [numStr] = meter.split('/');
    const beatsPerMeasure = parseInt(numStr, 10) || 4;
    const unitsPerMeasure = beatsPerMeasure * (meter.endsWith('/8') ? 1 : 2);

    if (isMultiVoice) {
        // Group voices by staff for %%score directive
        const staffMap = new Map();
        options.voices.forEach(v => {
            const sIdx = v.staffIndex !== undefined ? v.staffIndex : 0;
            if (!staffMap.has(sIdx)) staffMap.set(sIdx, []);
            staffMap.get(sIdx).push(v.id);
        });

        const scoreGroups = [];
        staffMap.forEach(vIds => {
            if (vIds.length > 1) {
                scoreGroups.push(`(${vIds.join(' ')})`);
            } else {
                scoreGroups.push(vIds[0]);
            }
        });
        abc += `%%score ${scoreGroups.join(' ')}\n`;

        options.voices.forEach(v => {
            let clefStr = 'clef=treble';
            if (v.clef === 'bass') clefStr = 'clef=bass';
            else if (v.clef === 'alto' || v.clef === 'c') clefStr = 'clef=alto';
            else if (v.clef === 'tenor') clefStr = 'clef=tenor';
            else if (v.clef === 'percussion') clefStr = 'clef=perc';
            const instStr = v.instrument ? ` instrument="${v.instrument}"` : '';
            abc += `V:${v.id} ${clefStr} name="${v.name || v.id}"${instStr}\n`;
        });

        abc += `K:${key}\n\n`;

        // Output voice blocks measure by measure or per voice
        options.voices.forEach(v => {
            const voiceAbc = generateAbcNotesWithLyrics(v.notes || [], unitsPerMeasure);
            abc += `[V:${v.id}] ${voiceAbc.notesLine}\n`;
            if (voiceAbc.lyricsLine) {
                abc += `w: ${voiceAbc.lyricsLine}\n`;
            }
            if (voiceAbc.lyricsLine2) {
                abc += `w: ${voiceAbc.lyricsLine2}\n`;
            }
        });

        return abc;
    }

    const primaryClef = options.clef || (options.voices && options.voices[0] && options.voices[0].clef) || 'treble';
    let clefDirective = '';
    if (primaryClef && primaryClef !== 'treble') {
      clefDirective = ` clef=${primaryClef === 'percussion' ? 'perc' : primaryClef}`;
    }
    abc += `K:${key}${clefDirective}\n`;
    const singleNotes = Array.isArray(notesOrVoices) ? notesOrVoices : (options.notes || []);
    const singleAbc = generateAbcNotesWithLyrics(singleNotes, unitsPerMeasure);
    abc += singleAbc.notesLine + '\n';
    if (singleAbc.lyricsLine) {
        abc += `w: ${singleAbc.lyricsLine}\n`;
    }
    if (singleAbc.lyricsLine2) {
        abc += `w: ${singleAbc.lyricsLine2}\n`;
    }
    return abc;
}

function generateAbcNotesWithLyrics(notes, unitsPerMeasure) {
    let currentMeasureUnits = 0;
    let measureTokens = [];
    let lyricTokens = [];
    let hasAnyLyrics = false;
    let lyricTokens2 = [];
    let hasAnyLyrics2 = false;

    notes.forEach((note) => {
        const tupletRatio = note.tuplet === 3 ? (2 / 3) : 1;
        const quarterBeats = durationToQuarterBeats(note.duration, note.isDotted, tupletRatio, note.dots || 0);
        const mrSpan = (!note.isHidden && note.isRest && note.multiRest > 1) ? Math.floor(note.multiRest) : 0;
        // A multi-measure rest fills whole measures regardless of its duration field
        const units = mrSpan > 0 ? unitsPerMeasure : quarterBeats * 2; // in 1/8 units

        let noteToken = '';
        let prefix = '';
        if (note.navigation) {
            prefix += `!${note.navigation}!`;
        }
        if (note.chord) {
            prefix += `"${note.chord}"`;
        }
        if (note.dynamic) {
            prefix += `!${note.dynamic}!`;
        }
        if (note.hairpin) {
            if (note.hairpin === 'crescendo-start') prefix += '!<(!';
            else if (note.hairpin === 'crescendo-end') prefix += '!<)!';
            else if (note.hairpin === 'diminuendo-start') prefix += '!>(!';
            else if (note.hairpin === 'diminuendo-end') prefix += '!>)!';
        }
        if (note.ornament) {
            if (note.ornament === 'trill') prefix += '!trill!';
            else if (note.ornament === 'mordent') prefix += '!mordent!';
            else if (note.ornament === 'turn') prefix += '!turn!';
            else if (note.ornament === 'pralltriller') prefix += '!pralltriller!';
        }
        if (note.articulation) {
            if (note.articulation === 'staccato') prefix += '.';
            else if (note.articulation === 'accent') prefix += '!>!';
            else if (note.articulation === 'marcato') prefix += '!^!';
            else if (note.articulation === 'fermata') prefix += 'H';
            else if (note.articulation === 'tenuto') prefix += '!tenuto!';
            else if (note.articulation === 'downbow') prefix += '!downbow!';
            else if (note.articulation === 'upbow') prefix += '!upbow!';
        }
        if (note.tupletStart) {
            prefix += `(${note.tuplet || 3}`;
        }
        if (note.grace) {
            prefix += `{${note.grace}}`;
        }

        if (note.isRest) {
            if (note.isHidden) {
                noteToken = prefix + (units === 1 ? 'x' : `x${units}`);
            } else if (note.multiRest > 1) {
                // Multi-measure rest: ZN spans N full measures
                noteToken = prefix + `Z${Math.floor(note.multiRest)}`;
            } else {
                noteToken = prefix + (units === 1 ? 'z' : `z${units}`);
            }
            } else {
                const pitchPart = abcPitchToken(note.step, note.octave, note.accidental, note.cautionary);
                const clusterExtras = (note.pitches || []).filter(p => p && p.step);
                const pitchToken = clusterExtras.length > 0
                    ? `[${pitchPart}${clusterExtras.map(p => abcPitchToken(p.step, p.octave, p.accidental, false)).join('')}]`
                    : pitchPart;

            let dur = '';
            if (units === 1) dur = '';
            else if (units === 0.25) dur = '/4';
            else if (units === 0.5) dur = '/2';
            else if (Number.isInteger(units)) dur = String(units);
            else if (units === 1.5) dur = '3/2';
            else if (units === 0.75) dur = '3/4';
            else if (Math.abs(units * 2 - Math.round(units * 2)) < 1e-9) dur = `${Math.round(units * 2)}/2`;
            else if (Math.abs(units * 8 - Math.round(units * 8)) < 1e-9) dur = `${Math.round(units * 8)}/8`;
            else dur = String(units);

            let tieSymbol = (note.tie || note.hasTie) ? '-' : '';
            let slurStart = note.slurStart ? '(' : '';
            let slurEnd = note.slurEnd ? ')' : '';
            let gliss = note.glissando ? '!glissando!' : '';

                noteToken = `${prefix}${slurStart}${gliss}${pitchToken}${dur}${tieSymbol}${slurEnd}`;
        }

        measureTokens.push(noteToken);
        if (note.lyric) {
            hasAnyLyrics = true;
            lyricTokens.push(note.lyric + (note.lyricMelisma ? '-' : ''));
        } else if (!note.isRest) {
            lyricTokens.push('*');
        }
        if (note.lyric2) {
            hasAnyLyrics2 = true;
            lyricTokens2.push(note.lyric2 + (note.lyric2Melisma ? '-' : ''));
        } else if (!note.isRest) {
            lyricTokens2.push('*');
        }

        currentMeasureUnits += units;

        if (currentMeasureUnits >= unitsPerMeasure) {
            measureTokens.push('|');
            if (hasAnyLyrics) {
                lyricTokens.push('|');
            }
            if (hasAnyLyrics2) {
                lyricTokens2.push('|');
            }
            currentMeasureUnits = 0;
        }

        // Multi-measure rest: close the remaining spanned measures with bars
        if (mrSpan > 1) {
            for (let b = 1; b < mrSpan; b++) {
                measureTokens.push('|');
                if (hasAnyLyrics) lyricTokens.push('|');
                if (hasAnyLyrics2) lyricTokens2.push('|');
            }
            currentMeasureUnits = 0;
        }
    });

    if (measureTokens.length > 0 && measureTokens[measureTokens.length - 1] !== '|') {
        measureTokens.push('|');
    }

    return {
        notesLine: measureTokens.join(' '),
        lyricsLine: hasAnyLyrics ? lyricTokens.join(' ') : null,
        lyricsLine2: hasAnyLyrics2 ? lyricTokens2.join(' ') : null
    };
}

/**
 * Parse ABC Notation string into clean notes or multi-voice structure
 */
export function abcToNotes(abcText) {
    const lines = abcText.split('\n');
    let defaultLength = 1 / 8;
    let timeSignature = '4/4';
    let key = 'C';
    let keyClef = 'treble';
    let title = 'Imported ABC';
    let composer = '';
    let tempo = 120;
    let scoreDirectives = '';

    const voiceDefinitions = new Map(); // id -> { id, name, clef, notes: [] }
    const voiceLineTokens = new Map(); // id -> array of raw music tokens
    const voiceLyricTokens = new Map(); // id -> array of raw lyrics lines

    let currentVoiceId = null;

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) continue;

        if (line.startsWith('%%score')) {
            scoreDirectives = line.substring(7).trim();
            continue;
        }

        if (line.startsWith('%')) continue;

        if (line.startsWith('T:')) title = line.substring(2).trim();
        else if (line.startsWith('C:')) composer = line.substring(2).trim();
        else if (line.startsWith('M:')) {
            const mStr = line.substring(2).trim();
            if (mStr === 'C') timeSignature = '4/4';
            else if (mStr === 'C|') timeSignature = '2/2';
            else timeSignature = mStr;
        }
        else if (line.startsWith('K:')) {
            const kLine = line.substring(2).trim();
            const clefMatch = kLine.match(/clef=([a-zA-Z0-9_-]+)/i);
            if (clefMatch) {
                const cVal = clefMatch[1].toLowerCase();
                if (cVal === 'perc' || cVal === 'percussion') keyClef = 'percussion';
                else if (cVal === 'bass') keyClef = 'bass';
                else if (cVal === 'alto') keyClef = 'alto';
                else if (cVal === 'tenor') keyClef = 'tenor';
                else keyClef = 'treble';
            }
            key = kLine.replace(/clef=[^s]+/i, '').trim() || 'C';
        }
        else if (line.startsWith('Q:')) {
            const qStr = line.substring(2).trim();
            const bpmMatch = qStr.match(/(\d+)\s*$/) || qStr.match(/=\s*(\d+)/);
            if (bpmMatch) {
                tempo = parseInt(bpmMatch[1], 10) || 120;
            }
        } else if (line.startsWith('L:')) {
            const lStr = line.substring(2).trim();
            if (lStr === '1/4') defaultLength = 1 / 4;
            else if (lStr === '1/8') defaultLength = 1 / 8;
            else if (lStr === '1/16') defaultLength = 1 / 16;
            else if (lStr === '1/2') defaultLength = 1 / 2;
        } else if (line.startsWith('V:')) {
            // Voice Header Definition e.g. V:T1 clef=treble-8 name="Tenore I"
            const vHeader = line.substring(2).trim();
            const vParts = vHeader.split(/\s+/);
            const vId = vParts[0];

            let vName = vId;
            let vClef = 'treble';
            let vInstrument = 'acoustic_grand_piano';

            const nameMatch = vHeader.match(/name="([^"]+)"/) || vHeader.match(/name=([^\s]+)/) || vHeader.match(/nm="([^"]+)"/);
            if (nameMatch) vName = nameMatch[1];

            const instMatch = vHeader.match(/instrument="([^"]+)"/) || vHeader.match(/inst="([^"]+)"/) || vHeader.match(/instrument=([^\s]+)/);
            if (instMatch) vInstrument = instMatch[1];

            const clefMatch = vHeader.match(/clef=([^\s]+)/);
            if (clefMatch) {
                const c = clefMatch[1].toLowerCase();
                if (c.includes('bass')) vClef = 'bass';
                else if (c.includes('alto') || c === 'c') vClef = 'alto';
                else if (c.includes('tenor')) vClef = 'tenor';
                else vClef = 'treble';
            }

            if (!voiceDefinitions.has(vId)) {
                voiceDefinitions.set(vId, {
                    id: vId,
                    name: vName,
                    clef: vClef,
                    instrument: vInstrument,
                    staffIndex: 0,
                    stemDefault: 'auto',
                    notes: []
                });
                voiceLineTokens.set(vId, []);
                voiceLyricTokens.set(vId, []);
            }
            currentVoiceId = vId;
        } else if (line.startsWith('w:')) {
            // Lyrics line associated with current voice
            const lyricContent = line.substring(2).trim();
            const targetVoice = currentVoiceId || 'V1';
            if (!voiceLyricTokens.has(targetVoice)) {
                voiceLyricTokens.set(targetVoice, []);
            }
            voiceLyricTokens.get(targetVoice).push(lyricContent);
        } else if (!line.match(/^[A-Z]:/)) {
            // Music content line - check for inline [V:id] or append to current voice
            if (line.includes('[V:')) {
                // Split inline voice segments strictly by [V:
                const segments = line.split(/\[V:/);
                segments.forEach(seg => {
                    const trimmedSeg = seg.trim();
                    if (!trimmedSeg) return;

                    const closeBracketIdx = trimmedSeg.indexOf(']');
                    if (closeBracketIdx !== -1) {
                        const vId = trimmedSeg.substring(0, closeBracketIdx).trim();
                        const musicPart = trimmedSeg.substring(closeBracketIdx + 1).trim();

                        if (!voiceDefinitions.has(vId)) {
                            voiceDefinitions.set(vId, {
                                id: vId,
                                name: vId,
                                clef: vId.toLowerCase().startsWith('b') ? 'bass' : 'treble',
                                staffIndex: 0,
                                stemDefault: 'auto',
                                notes: []
                            });
                            voiceLineTokens.set(vId, []);
                            voiceLyricTokens.set(vId, []);
                        }
                        currentVoiceId = vId;
                        if (musicPart) {
                            voiceLineTokens.get(vId).push(musicPart);
                        }
                    } else if (currentVoiceId) {
                        voiceLineTokens.get(currentVoiceId).push(trimmedSeg);
                    }
                });
            } else {
                const targetVoice = currentVoiceId || 'V1';
                if (!voiceDefinitions.has(targetVoice)) {
                    voiceDefinitions.set(targetVoice, {
                        id: targetVoice,
                        name: 'Melodia',
                        clef: 'treble',
                        staffIndex: 0,
                        stemDefault: 'auto',
                        notes: []
                    });
                    voiceLineTokens.set(targetVoice, []);
                    voiceLyricTokens.set(targetVoice, []);
                }
                voiceLineTokens.get(targetVoice).push(line);
            }
        }
    }

    // Parse score grouping if scoreDirectives is available, e.g. "(T1 T2) (B1 B2)"
    if (scoreDirectives) {
        // Find parenthesized groups or standalone voice names
        const groupMatches = scoreDirectives.match(/\([^)]+\)|[^\s()]+/g) || [];
        groupMatches.forEach((groupStr, staffIdx) => {
            const cleanGroup = groupStr.replace(/[()]/g, '').trim();
            const voiceIdsInGroup = cleanGroup.split(/\s+/);
            voiceIdsInGroup.forEach((vId, idxInStaff) => {
                if (voiceDefinitions.has(vId)) {
                    const vObj = voiceDefinitions.get(vId);
                    vObj.staffIndex = staffIdx;
                    if (voiceIdsInGroup.length > 1) {
                        vObj.stemDefault = idxInStaff === 0 ? 'up' : 'down';
                    }
                }
            });
        });
    } else {
        // Automatic grouping by clef / index
        let hasBass = false;
        voiceDefinitions.forEach(v => {
            if (v.clef === 'bass') hasBass = true;
        });

        let staffCounter = 0;
        voiceDefinitions.forEach(v => {
            if (voiceDefinitions.size > 1 && hasBass) {
                v.staffIndex = v.clef === 'bass' ? 1 : 0;
            } else {
                v.staffIndex = staffCounter++;
            }
        });
    }

    // Process notes and lyrics for each voice
    const voicesResult = [];
    voiceDefinitions.forEach((vDef, vId) => {
        const rawMusicString = (voiceLineTokens.get(vId) || []).join(' ');
        const voiceNotes = parseAbcTokensToNotes(rawMusicString, defaultLength);

        // Apply lyrics if available (1st w: line -> verse 1, 2nd -> verse 2)
        const rawLyricsLines = voiceLyricTokens.get(vId) || [];
        rawLyricsLines.slice(0, 2).forEach((rawLyricString, verseIdx) => {
            const lyricTokens = rawLyricString.split(/\s+/).filter(t => t.length > 0);
            const key = verseIdx === 0 ? 'lyric' : 'lyric2';
            let noteIdx = 0;
            for (const lToken of lyricTokens) {
                if (lToken === '|' || lToken === '||' || lToken === ':|' || lToken === '|:') {
                    continue; // Skip bar markers in lyric line
                }
                while (noteIdx < voiceNotes.length && voiceNotes[noteIdx].isRest) {
                    noteIdx++; // Skip rests
                }
                if (noteIdx < voiceNotes.length) {
                    if (lToken !== '*' && lToken !== '_') {
                        // Trailing hyphen = melisma (extended syllable)
                        const mel = /-$/.test(lToken);
                        voiceNotes[noteIdx][key] = lToken.replace(/-$/, '');
                        if (mel) voiceNotes[noteIdx][key + 'Melisma'] = true;
                    }
                    noteIdx++;
                }
            }
        });

        vDef.notes = voiceNotes;
        voicesResult.push(vDef);
    });

    const primaryNotes = voicesResult.length > 0 ? voicesResult[0].notes : [];

    if (voicesResult.length === 1 && keyClef) {
        voicesResult[0].clef = keyClef;
    }
    return {
        voices: voicesResult,
        clef: keyClef,
        notes: primaryNotes,
        title,
        composer,
        key,
        timeSignature,
        tempo,
        isMultiVoice: voicesResult.length > 1
    };
}

function parseAbcTokensToNotes(musicContent, defaultLength) {
    let content = musicContent;

    // Simplify Chords in square brackets: [GBd]2 -> G2, recording stacked
    // extras so they can be re-attached to the parsed main note below.
    const clusterQueue = [];
    content = content.replace(/\[([_^=]?[a-gA-G][,']*(?:[_^=]*[a-gA-G][,']*)+)\]([0-9/]*)/g, (match, inner, dur) => {
        const parts = inner.match(/[_^=]*[a-gA-G][,']*/g) || [];
        if (parts.length < 2) return match;
        clusterQueue.push({ main: parts[0], extras: parts.slice(1) });
        return parts[0] + (dur || '');
    });
    content = content.replace(/\[([_^=]?[a-gA-G][,']*)[^\]]*\]([0-9/]*)/g, (match, p1, p2) => p1 + (p2 || ''));

    // Normalize voltas: :|1 -> :| [1 , |1 -> | [1 , |2 -> | [2
    content = content.replace(/:\|(\d)/g, (match, d) => ' :| [' + d + ' ');
    content = content.replace(/\|(\d)/g, (match, d) => ' | [' + d + ' ');

    // Tokenize notes, rests, ties, slurs, chords, dynamics, articulations, tuplets, grace notes and navigation marks
    const tokenRegex = /("[^"]+"|![^!]+!|\{[^}]+\}|\(\d+|H|\.|\|:|:\|:|::|:\||\[\||\|\]|\|\||\||\[\d|\(|\)|\(?[zxZX][0-9/.]*\)?|-|\(?(\(\?[_^=]+\))?[_^=]*[a-gA-G][,']*[0-9/.]*-?\)?-?)/g;
    const tokens = content.match(tokenRegex) || [];
    const notes = [];

    let pendingStartBar = null; // e.g. '|:'
    let pendingVolta = null;    // e.g. 1, 2
    let pendingSlurStart = false;
    let pendingTuplet = null;   // e.g. 3, 5, 6
    let activeTupletCount = 0;
    let pendingGrace = null;
    let pendingGlissando = false;
    let pendingChord = null;
    let pendingDynamic = null;
    let pendingArticulation = null;
    let pendingOrnament = null;
    let pendingHairpin = null;
    let pendingNavigation = null;

    for (let token of tokens) {
        // Grace notes e.g. {g}, {f#}, {c/}
        if (token.startsWith('{') && token.endsWith('}')) {
            pendingGrace = token.slice(1, -1);
            continue;
        }

        // Tuplet indicators e.g. (3, (5, (6
        if (token.match(/^\(\d+$/)) {
            pendingTuplet = parseInt(token.substring(1), 10) || 3;
            activeTupletCount = pendingTuplet;
            continue;
        }

        // Chord symbol e.g. "G", "Em7"
        if (token.startsWith('"') && token.endsWith('"') && token.length >= 2) {
            pendingChord = token.slice(1, -1);
            continue;
        }

        // Glissando directive
        if (token.toLowerCase() === '!glissando!' || token.toLowerCase() === '!gliss!') {
            pendingGlissando = true;
            continue;
        }

        // Hairpins / Wedges e.g. !<(!, !<)!, !>(!, !>)!
        if (token === '!<!' || token === '!<(!' || token === '!crescendo(!') {
            pendingHairpin = 'crescendo-start';
            continue;
        } else if (token === '!<)!' || token === '!crescendo)!') {
            pendingHairpin = 'crescendo-end';
            continue;
        } else if (token === '!>!' || token === '!>(!' || token === '!diminuendo(!') {
            pendingHairpin = 'diminuendo-start';
            continue;
        } else if (token === '!>)!' || token === '!diminuendo)!') {
            pendingHairpin = 'diminuendo-end';
            continue;
        }

        // Dynamics or inline directives e.g. !p!, !f!, !staccato!, !fermata!, !tenuto!, !accent!, !marcato!, !trill!, !turn!, !mordent!
        if (token.startsWith('!') && token.endsWith('!') && token.length >= 2) {
            const inner = token.slice(1, -1).toLowerCase();
            if (['p', 'f', 'mf', 'mp', 'ff', 'pp', 'fff', 'ppp'].includes(inner)) {
                pendingDynamic = inner;
            } else if (inner === 'staccato' || inner === '.') {
                pendingArticulation = 'staccato';
            } else if (inner === 'fermata') {
                pendingArticulation = 'fermata';
            } else if (inner === 'accent' || inner === '>') {
                pendingArticulation = 'accent';
            } else if (inner === 'marcato' || inner === '^') {
                pendingArticulation = 'marcato';
            } else if (inner === 'tenuto' || inner === '-') {
                pendingArticulation = 'tenuto';
            } else if (inner === 'downbow' || inner === 'v') {
                pendingArticulation = 'downbow';
            } else if (inner === 'upbow' || inner === 'u') {
                pendingArticulation = 'upbow';
            } else if (inner === 'trill' || inner === 'tr') {
                pendingOrnament = 'trill';
            } else if (inner === 'mordent' || inner === '~') {
                pendingOrnament = 'mordent';
            } else if (inner === 'turn') {
                pendingOrnament = 'turn';
            } else if (inner === 'pralltriller') {
                pendingOrnament = 'pralltriller';
            } else if (inner === 'coda' || inner === 'segno' || inner === 'fine' || inner.includes('d.c.') || inner.includes('d.s.')) {
                pendingNavigation = inner;
            }
            continue;
        }

        // Standalone ornament shorthand T (trill) or ~ (mordent/turn)
        if (token === 'T' || token === 'tr') {
            pendingOrnament = 'trill';
            continue;
        }
        if (token === '~') {
            pendingOrnament = 'turn';
            continue;
        }

        // Fermata shorthand H
        if (token === 'H') {
            pendingArticulation = 'fermata';
            continue;
        }

        // Standalone dot (staccato)
        if (token === '.') {
            pendingArticulation = 'staccato';
            continue;
        }

        if (token === '(') {
            pendingSlurStart = true;
            continue;
        }
        if (token === ')') {
            if (notes.length > 0) {
                notes[notes.length - 1].slurEnd = true;
            }
            continue;
        }
        if (token === '-') {
            if (notes.length > 0) {
                notes[notes.length - 1].hasTie = true;
            }
            continue;
        }

        if (token === '|:' || token === '::' || token === ':|:') {
            pendingStartBar = '|:';
            if (notes.length > 0) {
                notes[notes.length - 1].endRepeat = true;
            }
            continue;
        } else if (token === ':|') {
            if (notes.length > 0) {
                notes[notes.length - 1].endRepeat = true;
            }
            continue;
        } else if (token.startsWith('[')) {
            const voltaNum = parseInt(token.substring(1), 10);
            if (!isNaN(voltaNum)) {
                pendingVolta = voltaNum;
                continue;
            }
            if (token === '[|' || token === '|]') {
                continue;
            }
        } else if (token.includes('|')) {
            continue;
        }

        let isStartRepeat = false;
        if (pendingStartBar) {
            isStartRepeat = true;
            pendingStartBar = null;
        }

        let volta = null;
        if (pendingVolta !== null) {
            volta = pendingVolta;
            pendingVolta = null;
        }

        // Handle attached open parenthesis e.g. (G4 or ((G4
        while (token.startsWith('(')) {
            pendingSlurStart = true;
            token = token.substring(1);
        }

        // Handle attached close parenthesis or ties at end e.g. B2), d2)-, D4-
        let hasAttachedSlurEnd = false;
        let hasTie = false;
        let changed = true;
        while (changed) {
            changed = false;
            if (token.endsWith(')')) {
                hasAttachedSlurEnd = true;
                token = token.slice(0, -1);
                changed = true;
            }
            if (token.endsWith('-')) {
                hasTie = true;
                token = token.slice(0, -1);
                changed = true;
            }
        }

        let slurStart = false;
        if (pendingSlurStart) {
            slurStart = true;
            pendingSlurStart = false;
        }

        const noteChord = pendingChord;
        pendingChord = null;
        const noteDynamic = pendingDynamic;
        pendingDynamic = null;
        const noteArticulation = pendingArticulation;
        pendingArticulation = null;
        const noteOrnament = pendingOrnament;
        pendingOrnament = null;
        const noteHairpin = pendingHairpin;
        pendingHairpin = null;
        const noteNavigation = pendingNavigation;
        pendingNavigation = null;

        let tupletVal = null;
        let isTupletStart = false;
        let isTupletEnd = false;
        if (activeTupletCount > 0) {
            tupletVal = pendingTuplet || 3;
            if (activeTupletCount === (pendingTuplet || 3)) {
                isTupletStart = true;
            }
            activeTupletCount--;
            if (activeTupletCount === 0) {
                isTupletEnd = true;
                pendingTuplet = null;
            }
        }

        if (token.startsWith('z') || token.startsWith('Z') || token.startsWith('x') || token.startsWith('X')) {
            const isHidden = token.startsWith('x') || token.startsWith('X');
            const durStr = token.substring(1);
            const { duration, isDotted, dots: restDots } = parseAbcDuration(durStr, defaultLength);
            // Multi-measure rest: ZN spans N full measures (bare Z = whole rest)
            const mrCount = token.startsWith('Z') ? parseInt(durStr, 10) : NaN;
            const multiRest = !isHidden && !isNaN(mrCount) && mrCount >= 2 ? mrCount : undefined;
            notes.push({
                step: 'B',
                octave: 4,
                pitch: 'B4',
                accidental: '',
                duration,
                isDotted,
                dots: restDots || 0,
                isRest: true,
                isHidden: isHidden,
                ...(multiRest ? { multiRest } : {}),
                chord: noteChord,
                dynamic: noteDynamic,
                articulation: noteArticulation,
                ornament: noteOrnament,
                hairpin: noteHairpin,
                navigation: noteNavigation,
                tuplet: tupletVal,
                tupletStart: isTupletStart,
                tupletEnd: isTupletEnd,
                startRepeat: isStartRepeat,
                volta: volta,
                hasTie: false,
                slurStart: false,
                slurEnd: hasAttachedSlurEnd
            });
            continue;
        }

        let remaining = token;
        let accidental = '';
        let isCautionary = false;

        if (remaining.startsWith('(?') && remaining.includes(')')) {
            isCautionary = true;
            const endParen = remaining.indexOf(')');
            const innerAcc = remaining.substring(2, endParen);
            if (innerAcc === '^^') accidental = '##';
            else if (innerAcc === '^') accidental = '#';
            else if (innerAcc === '__') accidental = 'bb';
            else if (innerAcc === '_') accidental = 'b';
            else if (innerAcc === '=') accidental = '=';
            remaining = remaining.substring(endParen + 1);
        } else if (remaining.startsWith('^^')) {
            accidental = '##';
            remaining = remaining.substring(2);
        } else if (remaining.startsWith('^')) {
            accidental = '#';
            remaining = remaining.substring(1);
        } else if (remaining.startsWith('__')) {
            accidental = 'bb';
            remaining = remaining.substring(2);
        } else if (remaining.startsWith('_')) {
            accidental = 'b';
            remaining = remaining.substring(1);
        } else if (remaining.startsWith('=')) {
            accidental = '=';
            remaining = remaining.substring(1);
        }

        const noteCharMatch = remaining.match(/^[a-gA-G]/);
        if (!noteCharMatch) continue;

        const noteChar = noteCharMatch[0];
        remaining = remaining.substring(1);

        let octave = 4;
        let step = noteChar.toUpperCase();
        if (noteChar === noteChar.toLowerCase()) {
            octave = 5;
        }

        while (remaining.startsWith(",")) {
            octave -= 1;
            remaining = remaining.substring(1);
        }
        while (remaining.startsWith("'")) {
            octave += 1;
            remaining = remaining.substring(1);
        }

        const { duration, isDotted, dots: noteDots } = parseAbcDuration(remaining, defaultLength);

        const currentGrace = pendingGrace;
        pendingGrace = null;
        const currentGlissando = pendingGlissando;
        pendingGlissando = false;

        notes.push({
            step,
            octave,
            pitch: `${step}${octave}`,
            accidental,
            cautionary: isCautionary,
            duration,
            isDotted,
            dots: noteDots || 0,
            isRest: false,
            chord: noteChord,
            dynamic: noteDynamic,
            articulation: noteArticulation,
            ornament: noteOrnament,
            hairpin: noteHairpin,
            navigation: noteNavigation,
            grace: currentGrace,
            glissando: currentGlissando,
            tuplet: tupletVal,
            tupletStart: isTupletStart,
            tupletEnd: isTupletEnd,
            startRepeat: isStartRepeat,
            volta: volta,
            hasTie,
            slurStart,
            slurEnd: hasAttachedSlurEnd
        });
    }

    // Re-attach stacked chord tones recorded from [...] groups, matching each
    // main pitch (step + octave) in order.
    let searchFrom = 0;
    clusterQueue.forEach(cl => {
        const mainPitch = parseAbcPitchPart(cl.main);
        if (!mainPitch) return;
        for (let i = searchFrom; i < notes.length; i++) {
            const n = notes[i];
            if (!n.isRest && n.step === mainPitch.step && n.octave === mainPitch.octave) {
                n.pitches = cl.extras.map(parseAbcPitchPart).filter(Boolean).map(p => ({
                    step: p.step,
                    octave: p.octave,
                    accidental: p.accidental
                }));
                searchFrom = i + 1;
                break;
            }
        }
    });

    return notes;
}

function parseAbcPitchPart(part) {
    const m = part.match(/^([_^=]*)([a-gA-G])([,']*)$/);
    if (!m) return null;
    const accRaw = m[1];
    const letter = m[2];
    const octMarks = m[3];
    let accidental = '';
    if (accRaw === '^^') accidental = '##';
    else if (accRaw === '^') accidental = '#';
    else if (accRaw === '__') accidental = 'bb';
    else if (accRaw === '_') accidental = 'b';
    else if (accRaw === '=') accidental = '=';
    const isLow = letter === letter.toLowerCase();
    let octave = isLow ? 5 : 4;
    for (const ch of octMarks) {
        if (ch === ',') octave -= 1;
        else octave += 1;
    }
    return { step: letter.toUpperCase(), octave, accidental };
}

function parseAbcDuration(durStr, defaultLength) {
    let multiplier = 1;
    let isExplicitDot = false;
    if (durStr) {
        if (durStr.includes('>')) {
            multiplier = 1.5;
            isExplicitDot = true;
        } else if (durStr === '/2' || durStr === '/') multiplier = 0.5;
        else if (durStr === '/4') multiplier = 0.25;
        else if (/^(\d+)\/(\d+)$/.test(durStr)) {
            // Generic fraction (e.g. 3/2, 7/2, 7/4): value relative to default length
            const [, num, den] = durStr.match(/^(\d+)\/(\d+)$/);
            multiplier = (parseInt(num, 10) || 1) / (parseInt(den, 10) || 1);
            isExplicitDot = true;
        } else if (durStr === '1.5') {
            multiplier = 1.5;
        } else if (durStr.startsWith('/')) {
            const div = parseFloat(durStr.substring(1)) || 2;
            multiplier = 1 / div;
        } else {
            multiplier = parseFloat(durStr) || 1;
        }
    }

    const totalQuarterBeats = (defaultLength * multiplier) / 0.25;

    // Check if totalQuarterBeats matches standard dotted durations
    // 6 quarter beats = dotted whole (4 * 1.5)
    // 3 quarter beats = dotted half (2 * 1.5)
    // 1.5 quarter beats = dotted quarter (1 * 1.5)
    // 0.75 quarter beats = dotted eighth (0.5 * 1.5)
    // 0.375 quarter beats = dotted 16th (0.25 * 1.5)
    // Double dots: 7 / 3.5 / 1.75 / 0.875 / 0.4375 / 0.21875 (x1.75)
    if (totalQuarterBeats >= 6.8) {
        return { duration: '1', isDotted: true, dots: 2 };
    }
    if (totalQuarterBeats >= 5.5) {
        return { duration: '1', isDotted: totalQuarterBeats >= 5.8, dots: totalQuarterBeats >= 5.8 ? 1 : 0 };
    }
    if (totalQuarterBeats >= 3.4 && totalQuarterBeats <= 3.6) {
        return { duration: '2', isDotted: true, dots: 2 }; // Double-dotted half (3.5)
    }
    if (totalQuarterBeats >= 3.5) {
        return { duration: '1', isDotted: false }; // Whole (4)
    }
    if (totalQuarterBeats >= 2.7) {
        return { duration: '2', isDotted: true }; // Dotted Half (3)
    }
    if (totalQuarterBeats >= 1.7 && totalQuarterBeats <= 1.8) {
        return { duration: '4', isDotted: true, dots: 2 }; // Double-dotted quarter (1.75)
    }
    if (totalQuarterBeats >= 1.75) {
        return { duration: '2', isDotted: false }; // Half (2)
    }
    if (totalQuarterBeats >= 1.3) {
        return { duration: '4', isDotted: true }; // Dotted Quarter (1.5)
    }
    if (totalQuarterBeats >= 0.85 && totalQuarterBeats <= 0.9) {
        return { duration: '8', isDotted: true, dots: 2 }; // Double-dotted 8th (0.875)
    }
    if (totalQuarterBeats >= 0.8) {
        return { duration: '4', isDotted: false }; // Quarter (1)
    }
    if (totalQuarterBeats >= 0.65) {
        return { duration: '8', isDotted: true }; // Dotted 8th (0.75)
    }
    if (totalQuarterBeats >= 0.42 && totalQuarterBeats <= 0.45) {
        return { duration: '16', isDotted: true, dots: 2 }; // Double-dotted 16th (0.4375)
    }
    if (totalQuarterBeats >= 0.4) {
        return { duration: '8', isDotted: false }; // 8th (0.5)
    }
    if (totalQuarterBeats >= 0.21 && totalQuarterBeats <= 0.23) {
        return { duration: '32', isDotted: true, dots: 2 }; // Double-dotted 32nd (0.21875)
    }
    if (totalQuarterBeats >= 0.2) {
        return { duration: '16', isDotted: isExplicitDot };
    }
    return { duration: '32', isDotted: isExplicitDot };
}

/**
 * Convert internal note array or voices to standard MusicXML string
 */
export function notesToMusicXml(notes, options = {}) {
    const title = options.title || 'Music Sheet Score';
    const meter = options.timeSignature || '4/4';
    const tempo = options.tempo || 120;
    const [beatsStr, beatTypeStr] = meter.split('/');
    const beats = parseInt(beatsStr, 10) || 4;
    const beatType = parseInt(beatTypeStr, 10) || 4;

    const divisions = 4; // 4 divisions per quarter note (16th note = 1 division)

    let xml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="4.0">
  <work>
    <work-title>${escapeXml(title)}</work-title>
  </work>
  <part-list>
    <score-part id="P1">
      <part-name>Music Sheet</part-name>
    </score-part>
  </part-list>
  <part id="P1">
`;

    const quarterBeatsPerMeasure = (beats * 4) / beatType;
    let currentQuarterBeats = 0;
    let measureIndex = 1;
    let isFirstMeasure = true;

    let measureNotes = [];

    // Multi-measure rest span of the measure being flushed (0 = none)
    let pendingMrSpan = 0;

    const flushMeasure = () => {
        xml += `    <measure number="${measureIndex}">\n`;
        // measure-style is a direct child of measure (not inside attributes)
        if (pendingMrSpan > 1) {
            xml += `      <measure-style>\n        <multiple-rest>${pendingMrSpan}</multiple-rest>\n      </measure-style>\n`;
        }
        if (isFirstMeasure) {
            const firstFifths = keyToFifths(options.key || 'C');
            xml += `      <attributes>\n`;
            xml += `        <divisions>${divisions}</divisions>\n`;
            xml += `        <key>\n`;
            xml += `          <fifths>${firstFifths !== null ? firstFifths : 0}</fifths>\n`;
            if (keyIsMinor(options.key || 'C')) xml += `          <mode>minor</mode>\n`;
            xml += `        </key>\n`;
            xml += `        <time>\n`;
            xml += `          <beats>${beats}</beats>\n`;
            xml += `          <beat-type>${beatType}</beat-type>\n`;
            xml += `        </time>\n`;
            xml += `        <clef>\n`;
            xml += `          <sign>G</sign>\n`;
            xml += `          <line>2</line>\n`;
            xml += `        </clef>\n`;
            xml += `      </attributes>\n`;
            xml += `      <direction placement="above">\n`;
            xml += `        <direction-type>\n`;
            xml += `          <metronome>\n`;
            xml += `            <beat-unit>quarter</beat-unit>\n`;
            xml += `            <per-minute>${tempo}</per-minute>\n`;
            xml += `          </metronome>\n`;
            xml += `        </direction-type>\n`;
            xml += `        <sound tempo="${tempo}"/>\n`;
            xml += `      </direction>\n`;
            isFirstMeasure = false;
        } else {
            // Mid-piece key/time changes (0-based measure = measureIndex - 1)
            const mIdx = measureIndex - 1;
            const kc = (options.keyChanges || []).find(e => e.measure === mIdx);
            const tc = (options.timeChanges || []).find(e => e.measure === mIdx);
            const tmpC = (options.tempoChanges || []).find(e => e.measure === mIdx);
            const tmpR = (options.tempoRamps || []).find(r => r.fromMeasure === mIdx);
            if (tmpC) {
                xml += `      <direction placement="above">\n        <direction-type>\n          <metronome>\n            <beat-unit>quarter</beat-unit>\n            <per-minute>${tmpC.bpm}</per-minute>\n          </metronome>\n        </direction-type>\n        <sound tempo="${tmpC.bpm}"/>\n      </direction>\n`;
            }
            if (tmpR) {
                const w = tmpR.kind === 'accel' ? 'accel.' : 'rit.';
                xml += `      <direction placement="above">\n        <direction-type>\n          <words font-style="italic" font-weight="bold">${w}</words>\n        </direction-type>\n      </direction>\n`;
            }
            if (kc || tc) {
                xml += `      <attributes>\n`;
                if (kc) {
                    const f = keyToFifths(kc.key);
                    if (f !== null) {
                        xml += `        <key>\n`;
                        xml += `          <fifths>${f}</fifths>\n`;
                        if (keyIsMinor(kc.key)) xml += `          <mode>minor</mode>\n`;
                        xml += `        </key>\n`;
                    }
                }
                if (tc && /^\d+\/\d+$/.test(tc.time || '')) {
                    const [tb, tbt] = tc.time.split('/');
                    xml += `        <time>\n`;
                    xml += `          <beats>${tb}</beats>\n`;
                    xml += `          <beat-type>${tbt}</beat-type>\n`;
                    xml += `        </time>\n`;
                }
                xml += `      </attributes>\n`;
            }
        }

        for (const note of measureNotes) {
            if (note.pedalStart) {
                xml += `      <direction placement="below">\n        <direction-type>\n          <pedal type="start"/>\n        </direction-type>\n      </direction>\n`;
            }
            if (note.pedalEnd) {
                xml += `      <direction placement="below">\n        <direction-type>\n          <pedal type="stop"/>\n        </direction-type>\n      </direction>\n`;
            }
            if (note.ottavaStart === '8va' || note.ottavaStart === '8vb') {
                const dir = note.ottavaStart === '8va' ? 'up' : 'down';
                xml += `      <direction placement="above">\n        <direction-type>\n          <octave-shift type="${dir}" size="8"/>\n        </direction-type>\n      </direction>\n`;
            }
            if (note.ottavaEnd) {
                xml += `      <direction placement="above">\n        <direction-type>\n          <octave-shift type="stop" size="8"/>\n        </direction-type>\n      </direction>\n`;
            }
            if (note.rehearsal) {
                xml += `      <direction placement="above">\n        <direction-type>\n          <rehearsal>${escapeXml(note.rehearsal)}</rehearsal>\n        </direction-type>\n      </direction>\n`;
            }
            if (note.navigation) {
                if (note.navigation === 'coda') {
                    xml += `      <direction placement="above">\n        <direction-type>\n          <coda/>\n        </direction-type>\n      </direction>\n`;
                } else if (note.navigation === 'segno') {
                    xml += `      <direction placement="above">\n        <direction-type>\n          <segno/>\n        </direction-type>\n      </direction>\n`;
                } else {
                    xml += `      <direction placement="above">\n        <direction-type>\n          <words font-style="italic" font-weight="bold">${escapeXml(note.navigation.toUpperCase())}</words>\n        </direction-type>\n      </direction>\n`;
                }
            }
            if (note.chord) {
                xml += `      <harmony>\n        <root><root-step>${escapeXml(note.chord.substring(0, 1))}</root-step></root>\n        <kind>${escapeXml(note.chord.substring(1) || 'major')}</kind>\n      </harmony>\n`;
            }
            if (note.hairpin) {
                if (note.hairpin === 'crescendo-start') {
                    xml += `      <direction placement="below">\n        <direction-type>\n          <wedge type="crescendo"/>\n        </direction-type>\n      </direction>\n`;
                } else if (note.hairpin === 'diminuendo-start') {
                    xml += `      <direction placement="below">\n        <direction-type>\n          <wedge type="diminuendo"/>\n        </direction-type>\n      </direction>\n`;
                } else if (note.hairpin === 'crescendo-end' || note.hairpin === 'diminuendo-end') {
                    xml += `      <direction placement="below">\n        <direction-type>\n          <wedge type="stop"/>\n        </direction-type>\n      </direction>\n`;
                }
            }
            if (note.dynamic) {
                xml += `      <direction placement="below">\n        <direction-type>\n          <dynamics><${escapeXml(note.dynamic)}/></dynamics>\n        </direction-type>\n      </direction>\n`;
            }

            const tupletRatio = note.tuplet === 3 ? (2 / 3) : 1;
            const qBeats = durationToQuarterBeats(note.duration, note.isDotted, tupletRatio, note.dots || 0);
            const durationDivs = Math.round(qBeats * divisions);
            const typeName = getMusicXmlType(note.duration);

            xml += `      <note>\n`;
            if (note.grace) {
                xml += `        <grace slash="yes"/>\n`;
            }
            if (note.isRest) {
                xml += `        <rest/>\n`;
            } else if (note.drum) {
                xml += `        <unpitched>\n`;
                xml += `          <display-step>${note.step}</display-step>\n`;
                xml += `          <display-octave>${note.octave}</display-octave>\n`;
                xml += `        </unpitched>\n`;
                xml += `        <instrument id="${note.drum}"/>\n`;
            } else {
                xml += `        <pitch>\n`;
                xml += `          <step>${note.step}</step>\n`;
                if (note.accidental === '##' || note.accidental === '^^') {
                    xml += `          <alter>2</alter>\n`;
                } else if (note.accidental === '#' || note.accidental === '^') {
                    xml += `          <alter>1</alter>\n`;
                } else if (note.accidental === 'bb' || note.accidental === '__') {
                    xml += `          <alter>-2</alter>\n`;
                } else if (note.accidental === 'b' || note.accidental === '_') {
                    xml += `          <alter>-1</alter>\n`;
                }
                xml += `          <octave>${note.octave}</octave>\n`;
                xml += `        </pitch>\n`;
                const cautionaryAttr = note.cautionary ? ' cautionary="yes"' : '';
                if (note.accidental === '##' || note.accidental === '^^') {
                    xml += `        <accidental${cautionaryAttr}>double-sharp</accidental>\n`;
                } else if (note.accidental === '#' || note.accidental === '^') {
                    xml += `        <accidental${cautionaryAttr}>sharp</accidental>\n`;
                } else if (note.accidental === 'bb' || note.accidental === '__') {
                    xml += `        <accidental${cautionaryAttr}>flat-flat</accidental>\n`;
                } else if (note.accidental === 'b' || note.accidental === '_') {
                    xml += `        <accidental${cautionaryAttr}>flat</accidental>\n`;
                } else if (note.accidental === '=') {
                    xml += `        <accidental${cautionaryAttr}>natural</accidental>\n`;
                }
            }
            xml += `        <duration>${durationDivs}</duration>\n`;
            xml += `        <type>${typeName}</type>\n`;
            if (note.isDotted) {
                xml += `        <dot/>\n`;
            }
            if ((note.dots || 0) >= 2) {
                xml += `        <dot/>\n`;
            }
            if (note.notehead === 'x' || note.notehead === 'diamond' || note.notehead === 'slash') {
                const xmlHead = note.notehead === 'x' ? 'x' : (note.notehead === 'diamond' ? 'diamond' : 'slash');
                xml += `        <notehead>${xmlHead}</notehead>\n`;
            }
            if (note.tuplet === 3) {
                xml += `        <time-modification>\n          <actual-notes>3</actual-notes>\n          <normal-notes>2</normal-notes>\n        </time-modification>\n`;
            }

            const notations = [];
            if (note.hasTie || note.tie) {
                notations.push(`          <tied type="start"/>`);
            }
            if (note.slurStart) {
                notations.push(`          <slur type="start" number="1"/>`);
            }
            if (note.slurEnd) {
                notations.push(`          <slur type="stop" number="1"/>`);
            }
            if (note.tupletStart) {
                notations.push(`          <tuplet type="start" number="1"/>`);
            }
            if (note.tupletEnd) {
                notations.push(`          <tuplet type="stop" number="1"/>`);
            }
            if (note.glissando) {
                notations.push(`          <glissando type="start" line-type="wavy"/>`);
            }
            if (note.arpeggio) {
                notations.push(`          <arpeggiate/>`);
            }
            if (note.tremolo >= 1 && note.tremolo <= 3) {
                notations.push(`          <ornaments><tremolo>${note.tremolo}</tremolo></ornaments>`);
            }
            if (note.breath) {
                notations.push(`          <articulations><breath-mark/></articulations>`);
            }
            if (note.ornament) {
                if (note.ornament === 'trill') notations.push(`          <ornaments><trill-mark/></ornaments>`);
                else if (note.ornament === 'turn') notations.push(`          <ornaments><turn/></ornaments>`);
                else if (note.ornament === 'mordent') notations.push(`          <ornaments><mordent/></ornaments>`);
                else if (note.ornament === 'pralltriller') notations.push(`          <ornaments><inverted-mordent/></ornaments>`);
            }
            if (note.articulation) {
                if (note.articulation === 'marcato') {
                    notations.push(`          <articulations><strong-accent type="up"/></articulations>`);
                } else if (note.articulation === 'downbow') {
                    notations.push(`          <technical><down-bow/></technical>`);
                } else if (note.articulation === 'upbow') {
                    notations.push(`          <technical><up-bow/></technical>`);
                } else {
                    notations.push(`          <articulations><${escapeXml(note.articulation)}/></articulations>`);
                }
            }
            if (note.tabSlide) {
                notations.push(`          <technical><slide type="start" line-type="solid"/></technical>`);
            }
            if (note.tabBend) {
                const alt = (note.tabBend === 'half' || note.tabBend === '1/2') ? 1 : ((note.tabBend === 'quarter' || note.tabBend === '1/4') ? 0.5 : (note.tabBend === '1 1/2' ? 3 : 2));
                notations.push(`          <technical><bend><bend-alter>${alt}</bend-alter></bend></technical>`);
            }
            if (note.tabHammer) {
                notations.push(`          <technical><hammer-on type="start">H</hammer-on></technical>`);
            }
            if (note.tabPull) {
                notations.push(`          <technical><pull-off type="start">P</pull-off></technical>`);
            }
            if (note.tabHarmonic) {
                notations.push(`          <technical><harmonic/></technical>`);
            }
            if (note.tabVibrato) {
                notations.push(`          <ornaments><wavy-line type="start"/></ornaments>`);
            }
            if (note.tabString && note.tabFret !== undefined) {
                notations.push(`          <technical><string>${note.tabString}</string><fret>${note.tabFret}</fret></technical>`);
            }

            if (notations.length > 0) {
                xml += `        <notations>\n${notations.join('\n')}\n        </notations>\n`;
            }

            if (note.lyric && !note.isRest) {
                xml += `        <lyric number="1">\n`;
                xml += `          <syllabic>${note.lyricMelisma ? 'begin' : 'single'}</syllabic>\n`;
                xml += `          <text>${escapeXml(note.lyric)}</text>\n`;
                if (note.lyricMelisma) xml += `          <extend/>\n`;
                xml += `        </lyric>\n`;
            }
            if (note.lyric2 && !note.isRest) {
                xml += `        <lyric number="2">\n`;
                xml += `          <syllabic>${note.lyric2Melisma ? 'begin' : 'single'}</syllabic>\n`;
                xml += `          <text>${escapeXml(note.lyric2)}</text>\n`;
                if (note.lyric2Melisma) xml += `          <extend/>\n`;
                xml += `        </lyric>\n`;
            }
            xml += `      </note>\n`;
            // Stacked chord tones: subsequent <note> elements with <chord/>
            if (!note.isRest && note.pitches && note.pitches.length > 0) {
                note.pitches.forEach(p => {
                    if (!p || !p.step) return;
                    const pOct = p.octave || 4;
                    const pAlter = p.accidental === '##' ? 2 : (p.accidental === '#' ? 1 : (p.accidental === 'bb' ? -2 : (p.accidental === 'b' ? -1 : 0)));
                    xml += `      <note>\n`;
                    xml += `        <chord/>\n`;
                    xml += `        <pitch>\n`;
                    xml += `          <step>${p.step}</step>\n`;
                    if (pAlter !== 0) xml += `          <alter>${pAlter}</alter>\n`;
                    xml += `          <octave>${pOct}</octave>\n`;
                    xml += `        </pitch>\n`;
                    xml += `        <duration>${durationDivs}</duration>\n`;
                    xml += `        <type>${typeName}</type>\n`;
                    xml += `      </note>\n`;
                });
            }
        }

        xml += `    </measure>\n`;
        measureIndex++;
        measureNotes = [];
        currentQuarterBeats = 0;
    };

    for (const note of notes) {
        let qBeats = durationToQuarterBeats(note.duration, note.isDotted, 1, note.dots || 0);
        const mrSpan = (!note.isHidden && note.isRest && note.multiRest > 1) ? Math.floor(note.multiRest) : 0;
        // Threshold follows mid-piece time changes (0-based measure = measureIndex - 1)
        const beatsThisMeasure = typeof options.measureBeatsAt === 'function'
            ? options.measureBeatsAt(measureIndex - 1)
            : quarterBeatsPerMeasure;
        if (mrSpan > 0) qBeats = mrSpan * beatsThisMeasure;
        measureNotes.push(note);
        currentQuarterBeats += qBeats;

        if (currentQuarterBeats >= beatsThisMeasure) {
            pendingMrSpan = mrSpan;
            flushMeasure();
            pendingMrSpan = 0;
            // Emit the remaining spanned measures as empty shells
            for (let k = 1; k < mrSpan; k++) {
                xml += `    <measure number="${measureIndex}">\n    </measure>\n`;
                measureIndex++;
            }
        }
    }

    if (measureNotes.length > 0) {
        flushMeasure();
    }

    xml += `  </part>\n</score-partwise>\n`;
    return xml;
}

function getMusicXmlType(duration) {
    switch (String(duration)) {
        case '1': return 'whole';
        case '2': return 'half';
        case '4': return 'quarter';
        case '8': return 'eighth';
        case '16': return '16th';
        case '32': return '32nd';
        default: return 'quarter';
    }
}

function durationFromMusicXmlType(typeStr) {
    switch ((typeStr || '').toLowerCase()) {
        case 'whole': return '1';
        case 'half': return '2';
        case 'quarter': return '4';
        case 'eighth': return '8';
        case '16th': return '16';
        case '32nd': return '32';
        default: return '4';
    }
}

/**
 * Parse MusicXML string into notes array
 */
export function musicXmlToNotes(xmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');

    const parserError = doc.querySelector('parsererror');
    if (parserError) {
        throw new Error('Falha ao processar MusicXML: ' + parserError.textContent);
    }

    let title = 'MusicXML Score';
    const titleNode = doc.querySelector('work-title') || doc.querySelector('movement-title');
    if (titleNode) title = titleNode.textContent.trim();

    let timeSignature = '4/4';
    const beatsNode = doc.querySelector('time > beats');
    const beatTypeNode = doc.querySelector('time > beat-type');
    if (beatsNode && beatTypeNode) {
        timeSignature = `${beatsNode.textContent.trim()}/${beatTypeNode.textContent.trim()}`;
    }

    let key = 'C';
    const fifthsNode = doc.querySelector('key > fifths');
    if (fifthsNode) {
        const fifths = parseInt(fifthsNode.textContent.trim(), 10);
        const circleOfFifths = {
            '-7': 'Cb', '-6': 'Gb', '-5': 'Db', '-4': 'Ab', '-3': 'Eb', '-2': 'Bb', '-1': 'F',
            '0': 'C', '1': 'G', '2': 'D', '3': 'A', '4': 'E', '5': 'B', '6': 'F#', '7': 'C#'
        };
        key = circleOfFifths[String(fifths)] || 'C';
    }

    let tempo = 120;
    const soundNode = doc.querySelector('sound[tempo]');
    const perMinuteNode = doc.querySelector('per-minute');
    if (soundNode && soundNode.getAttribute('tempo')) {
        tempo = parseInt(soundNode.getAttribute('tempo'), 10) || 120;
    } else if (perMinuteNode) {
        tempo = parseInt(perMinuteNode.textContent.trim(), 10) || 120;
    }

    const noteElements = doc.querySelectorAll('part > measure > note, part > measure > harmony, part > measure > direction, part > measure > measure-style');

    // Mid-piece key/time changes: per-measure <attributes> after the first measure.
    // Measure numbers are 1-based in XML; our events use 0-based measure indexes.
    const keyChanges = [];
    const timeChanges = [];
    const tempoChanges = [];
    const tempoRamps = [];
    doc.querySelectorAll('part > measure').forEach((mEl, idx) => {
        if (idx === 0) return;
        const kf = mEl.querySelector(':scope > attributes > key > fifths');
        if (kf) {
            const modeEl = mEl.querySelector(':scope > attributes > key > mode');
            const k = fifthsToKey(kf.textContent.trim(), modeEl ? modeEl.textContent.trim().toLowerCase() : 'major');
            if (k) keyChanges.push({ measure: idx, key: k });
        }
        const tb = mEl.querySelector(':scope > attributes > time > beats');
        const tbt = mEl.querySelector(':scope > attributes > time > beat-type');
        if (tb && tbt) {
            const t = `${tb.textContent.trim()}/${tbt.textContent.trim()}`;
            if (/^\d+\/\d+$/.test(t)) timeChanges.push({ measure: idx, time: t });
        }
        // Tempo: metronome mark -> sudden change; rit./accel. words -> ramp to end
        const metro = mEl.querySelector(':scope > direction > direction-type > metronome > per-minute');
        const soundEl = mEl.querySelector(':scope > direction > sound[tempo]');
        const metroBpm = metro ? parseInt(metro.textContent.trim(), 10) : (soundEl ? parseInt(soundEl.getAttribute('tempo'), 10) : NaN);
        if (idx > 0 && !isNaN(metroBpm) && metroBpm >= 30 && metroBpm <= 300) {
            tempoChanges.push({ measure: idx, bpm: metroBpm });
        }
        const wordsEls = mEl.querySelectorAll(':scope > direction > direction-type > words');
        wordsEls.forEach(wEl => {
            const wt = wEl.textContent.trim().toLowerCase();
            if (wt.startsWith('rit') || wt.startsWith('rall') || wt.startsWith('accel')) {
                tempoRamps.push({ fromMeasure: idx, toMeasure: null, toBpm: null, kind: wt.startsWith('accel') ? 'accel' : 'rit' });
            }
        });
    });
    // Resolve open-ended ramps: to piece end, target = 65% (rit.) / 140% (accel.) of bpm at start
    {
        const totalM = doc.querySelectorAll('part > measure').length;
        const bpmAt = (mm) => {
            let b = tempo || 120;
            tempoChanges.filter(e => e.measure <= mm).sort((a, c) => a.measure - c.measure).forEach(e => { b = e.bpm; });
            return b;
        };
        tempoRamps.forEach(r => {
            if (r.toMeasure === null || r.toMeasure === undefined) r.toMeasure = Math.max(r.fromMeasure + 1, totalM - 1);
            if (!r.toBpm) {
                const cur = bpmAt(r.fromMeasure);
                r.toBpm = Math.max(30, Math.min(300, Math.round(cur * (r.kind === 'accel' ? 1.4 : 0.65))));
            }
        });
    }

    const notes = [];
    let pendingChord = null;
    let pendingDynamic = null;
    let pendingHairpin = null;
    let pendingNavigation = null;
    let pendingRehearsal = null;
    let pendingMultiRest = 0;
    let pendingOttavaStart = null;
    let pendingPedalStart = false;

    noteElements.forEach(el => {
        if (el.tagName.toLowerCase() === 'harmony') {
            const rootStep = el.querySelector('root > root-step');
            const kind = el.querySelector('kind');
            if (rootStep) {
                const step = rootStep.textContent.trim();
                const kindText = kind ? kind.textContent.trim() : '';
                const kindClean = kindText === 'major' ? '' : (kindText === 'minor' ? 'm' : kindText);
                pendingChord = `${step}${kindClean}`;
            }
            return;
        }

        if (el.tagName.toLowerCase() === 'measure-style') {
            const mrEl = el.querySelector('multiple-rest');
            if (mrEl) {
                const n = parseInt(mrEl.textContent.trim(), 10);
                if (n >= 2) pendingMultiRest = n;
            }
            return;
        }

        if (el.tagName.toLowerCase() === 'direction') {
            const dynEl = el.querySelector('dynamics');
            if (dynEl && dynEl.firstElementChild) {
                pendingDynamic = dynEl.firstElementChild.tagName.toLowerCase();
            }
            const wedgeEl = el.querySelector('wedge');
            if (wedgeEl) {
                const wType = wedgeEl.getAttribute('type');
                if (wType === 'crescendo') pendingHairpin = 'crescendo-start';
                else if (wType === 'diminuendo') pendingHairpin = 'diminuendo-start';
                else if (wType === 'stop') pendingHairpin = 'crescendo-end';
            }
            const pedalEl = el.querySelector('pedal');
            if (pedalEl) {
                const pType = (pedalEl.getAttribute('type') || '').toLowerCase();
                if (pType === 'start') pendingPedalStart = true;
                else if (pType === 'stop' && notes.length > 0) {
                    const prev = notes[notes.length - 1];
                    if (prev) prev.pedalEnd = true;
                }
            }
            const octEl = el.querySelector('octave-shift');
            if (octEl) {
                const oType = (octEl.getAttribute('type') || '').toLowerCase();
                if (oType === 'up') pendingOttavaStart = '8va';
                else if (oType === 'down') pendingOttavaStart = '8vb';
                else if (oType === 'stop' && notes.length > 0) {
                    const prev = notes[notes.length - 1];
                    if (prev && !prev.isRest) prev.ottavaEnd = true;
                }
            }
            const rehEl = el.querySelector('rehearsal');
            if (rehEl && rehEl.textContent.trim()) pendingRehearsal = rehEl.textContent.trim().substring(0, 8);
            else if (el.querySelector('coda')) pendingNavigation = 'coda';
            else if (el.querySelector('segno')) pendingNavigation = 'segno';
            else {
                const wordsEl = el.querySelector('words');
                if (wordsEl) {
                    const text = wordsEl.textContent.trim().toLowerCase();
                    if (text.includes('to coda')) pendingNavigation = 'to coda';
                    else if (text.includes('fine')) pendingNavigation = 'fine';
                    else if (text.includes('d.c.')) pendingNavigation = 'd.c. al fine';
                    else if (text.includes('d.s.')) pendingNavigation = 'd.s. al coda';
                }
            }
            return;
        }

        const noteEl = el;
        // Stacked chord tone (<chord/>): attach its pitch to the previous note
        if (noteEl.querySelector('chord')) {
            const cs = noteEl.querySelector('pitch > step');
            const co = noteEl.querySelector('pitch > octave');
            const ca = noteEl.querySelector('pitch > alter');
            if (cs && notes.length > 0) {
                const prev = notes[notes.length - 1];
                if (prev && !prev.isRest) {
                    if (!prev.pitches) prev.pitches = [];
                    let acc = '';
                    if (ca) {
                        const av = parseInt(ca.textContent.trim(), 10);
                        if (av === 2) acc = '##';
                        else if (av === 1) acc = '#';
                        else if (av === -2) acc = 'bb';
                        else if (av === -1) acc = 'b';
                    }
                    prev.pitches.push({
                        step: cs.textContent.trim().toUpperCase(),
                        octave: co ? parseInt(co.textContent.trim(), 10) || 4 : 4,
                        accidental: acc
                    });
                }
            }
            return;
        }
        if (noteEl.querySelector('grace')) return;

        const isGrace = noteEl.querySelector('grace') !== null;
        const isRest = noteEl.querySelector('rest') !== null;
        const typeEl = noteEl.querySelector('type');
        const duration = typeEl ? durationFromMusicXmlType(typeEl.textContent.trim()) : (isGrace ? '8' : '4');
        const isDotted = noteEl.querySelector('dot') !== null;
        const dotCount = noteEl.querySelectorAll(':scope > dot').length;
        const dots = dotCount >= 2 ? 2 : (isDotted ? 1 : 0);

        const currentChord = pendingChord;
        pendingChord = null;
        const currentDynamic = pendingDynamic;
        pendingDynamic = null;
        const currentHairpin = pendingHairpin;
        pendingHairpin = null;
        const currentNavigation = pendingNavigation;
        pendingNavigation = null;
        const currentRehearsal = pendingRehearsal;
        pendingRehearsal = null;

        let articulation = null;
        if (noteEl.querySelector('notations > articulations > staccato')) articulation = 'staccato';
        else if (noteEl.querySelector('notations > articulations > accent')) articulation = 'accent';
        else if (noteEl.querySelector('notations > articulations > strong-accent')) articulation = 'marcato';
        else if (noteEl.querySelector('notations > articulations > tenuto')) articulation = 'tenuto';
        else if (noteEl.querySelector('notations > technical > down-bow')) articulation = 'downbow';
        else if (noteEl.querySelector('notations > technical > up-bow')) articulation = 'upbow';
        else if (noteEl.querySelector('notations > fermata')) articulation = 'fermata';

        let ornament = null;
        if (noteEl.querySelector('notations > ornaments > trill-mark')) ornament = 'trill';
        else if (noteEl.querySelector('notations > ornaments > turn')) ornament = 'turn';
        else if (noteEl.querySelector('notations > ornaments > mordent')) ornament = 'mordent';
        else if (noteEl.querySelector('notations > ornaments > inverted-mordent')) ornament = 'pralltriller';

        const glissandoEl = noteEl.querySelector('notations > glissando');
        const hasGlissando = glissandoEl !== null;
        const hasArpeggio = noteEl.querySelector('notations > arpeggiate') !== null;
        let tremoloVal = 0;
        const tremEl = noteEl.querySelector('notations > ornaments > tremolo');
        if (tremEl) {
            const tv = parseInt(tremEl.textContent.trim(), 10);
            if (tv >= 1 && tv <= 3) tremoloVal = tv;
        }
        const hasBreath = noteEl.querySelector('notations > articulations > breath-mark') !== null;

        const tupletEl = noteEl.querySelector('notations > tuplet');
        const tupletMod = noteEl.querySelector('time-modification > actual-notes');
        const tuplet = tupletMod ? parseInt(tupletMod.textContent.trim(), 10) : (tupletEl ? 3 : null);
        const tupletStart = !!(tupletEl && tupletEl.getAttribute('type') === 'start');
        const tupletEnd = !!(tupletEl && tupletEl.getAttribute('type') === 'stop');

        if (isRest) {
            notes.push({
                step: 'B',
                octave: 4,
                pitch: 'B4',
                accidental: '',
                duration: duration,
                isDotted,
                dots,
                isRest: true,
                ...(pendingMultiRest >= 2 ? { multiRest: pendingMultiRest } : {}),
                ...(pendingOttavaStart ? { ottavaStart: pendingOttavaStart } : {}),
                chord: currentChord,
                dynamic: currentDynamic,
                articulation,
                ornament,
                hairpin: currentHairpin,
                navigation: currentNavigation,
                ...(currentRehearsal ? { rehearsal: currentRehearsal } : {}),
                tuplet,
                tupletStart,
                tupletEnd
            });
            pendingMultiRest = 0;
            pendingOttavaStart = null;
            return;
        }

        const stepEl = noteEl.querySelector('pitch > step');
        const octaveEl = noteEl.querySelector('pitch > octave');
        const alterEl = noteEl.querySelector('pitch > alter');
        const accidentalEl = noteEl.querySelector('accidental');
        const unpitchedEl = noteEl.querySelector('unpitched');
        const instrumentEl = noteEl.querySelector('instrument');
        const drumId = instrumentEl ? (instrumentEl.getAttribute('id') || '').trim() : '';
        let drumStep = null;
        let drumOctave = null;
        if (unpitchedEl) {
            const dsEl = unpitchedEl.querySelector('display-step');
            const doEl = unpitchedEl.querySelector('display-octave');
            if (dsEl) drumStep = dsEl.textContent.trim().toUpperCase();
            if (doEl) drumOctave = parseInt(doEl.textContent.trim(), 10);
        }

        const step = drumStep || (stepEl ? stepEl.textContent.trim().toUpperCase() : 'C');
        const octave = drumOctave !== null && drumOctave !== undefined && !isNaN(drumOctave)
          ? drumOctave
          : (octaveEl ? parseInt(octaveEl.textContent.trim(), 10) : 4);

        let accidental = '';
        let isCautionary = false;
        if (accidentalEl && accidentalEl.getAttribute('cautionary') === 'yes') {
            isCautionary = true;
        }

        if (alterEl) {
            const alter = parseInt(alterEl.textContent.trim(), 10);
            if (alter === 2) accidental = '##';
            else if (alter === 1) accidental = '#';
            else if (alter === -2) accidental = 'bb';
            else if (alter === -1) accidental = 'b';
        } else if (accidentalEl) {
            const acc = accidentalEl.textContent.trim().toLowerCase();
            if (acc === 'double-sharp') accidental = '##';
            else if (acc === 'sharp') accidental = '#';
            else if (acc === 'flat-flat') accidental = 'bb';
            else if (acc === 'flat') accidental = 'b';
            else if (acc === 'natural') accidental = '=';
        }

        const lyricEls = noteEl.querySelectorAll('lyric');
        let lyric = '';
        let lyric2 = '';
        let lyricMelisma = false;
        let lyric2Melisma = false;
        lyricEls.forEach(el => {
            const num = el.getAttribute('number') || '1';
            const t = el.querySelector('text');
            if (!t) return;
            const hasExtend = el.querySelector('extend') !== null;
            if (num === '2') { lyric2 = t.textContent.trim(); lyric2Melisma = hasExtend; }
            else if (!lyric) { lyric = t.textContent.trim(); lyricMelisma = hasExtend; }
        });

        const tiedEl = noteEl.querySelector('notations > tied');
        const hasTie = !!(tiedEl && (tiedEl.getAttribute('type') === 'start' || !tiedEl.getAttribute('type')));

        const slurEl = noteEl.querySelector('notations > slur');
        const slurStart = !!(slurEl && slurEl.getAttribute('type') === 'start');
        const slurEnd = !!(slurEl && slurEl.getAttribute('type') === 'stop');

        const noteheadEl = noteEl.querySelector('note > notehead, notehead');
        const noteheadRaw = noteheadEl ? noteheadEl.textContent.trim().toLowerCase() : '';
        const notehead = (noteheadRaw === 'x' || noteheadRaw === 'diamond' || noteheadRaw === 'slash') ? noteheadRaw : undefined;

        const slideEl = noteEl.querySelector('notations > technical > slide');
        const tabSlide = slideEl ? (slideEl.getAttribute('type') === 'start' ? 'up' : true) : undefined;
        const bendEl = noteEl.querySelector('notations > technical > bend');
        let tabBend = undefined;
        if (bendEl) {
            const alterEl = bendEl.querySelector('bend-alter');
            const alt = alterEl ? parseFloat(alterEl.textContent.trim()) : 2;
            tabBend = alt === 1 ? '1/2' : (alt === 0.5 ? '1/4' : (alt === 3 ? '1 1/2' : 'full'));
        }
        const hammerEl = noteEl.querySelector('notations > technical > hammer-on');
        const tabHammer = !!(hammerEl && hammerEl.getAttribute('type') === 'start');
        const pullEl = noteEl.querySelector('notations > technical > pull-off');
        const tabPull = !!(pullEl && pullEl.getAttribute('type') === 'start');
        const harmonicEl = noteEl.querySelector('notations > technical > harmonic');
        const tabHarmonic = harmonicEl !== null;
        const vibratoEl = noteEl.querySelector('notations > ornaments > wavy-line');
        const tabVibrato = vibratoEl !== null;
        const stringEl = noteEl.querySelector('notations > technical > string');
        const fretEl = noteEl.querySelector('notations > technical > fret');
        const tabString = stringEl ? parseInt(stringEl.textContent.trim(), 10) : undefined;
        const tabFret = fretEl ? parseInt(fretEl.textContent.trim(), 10) : undefined;

        notes.push({
            step,
            octave,
            pitch: `${step}${octave}`,
            accidental,
            cautionary: isCautionary,
            duration,
            isDotted,
            dots,
            isRest: false,
            chord: currentChord,
            dynamic: currentDynamic,
            articulation,
            ornament,
            hairpin: currentHairpin,
            navigation: currentNavigation,
            ...(currentRehearsal ? { rehearsal: currentRehearsal } : {}),
            grace: isGrace ? 'g' : null,
            glissando: hasGlissando,
            tuplet,
            tupletStart,
            tupletEnd,
            lyric,
            ...(lyric2 ? { lyric2 } : {}),
            ...(lyricMelisma ? { lyricMelisma: true } : {}),
            ...(lyric2Melisma ? { lyric2Melisma: true } : {}),
            hasTie,
            slurStart,
            slurEnd,
            ...(notehead ? { notehead } : {}),
            ...(pendingOttavaStart ? { ottavaStart: pendingOttavaStart } : {}),
            ...(drumId ? { drum: drumId, ...(!notehead && ['hihat', 'hihatOpen', 'crash', 'ride'].includes(drumId) ? { notehead: 'x' } : {}) } : {}),
            ...(hasArpeggio ? { arpeggio: true } : {}),
            ...(tremoloVal ? { tremolo: tremoloVal } : {}),
            ...(hasBreath ? { breath: true } : {}),
            ...(pendingPedalStart ? { pedalStart: true } : {}),
            ...(tabSlide ? { tabSlide } : {}),
            ...(tabBend ? { tabBend } : {}),
            ...(tabHammer ? { tabHammer: true } : {}),
            ...(tabPull ? { tabPull: true } : {}),
            ...(tabHarmonic ? { tabHarmonic: true } : {}),
            ...(tabVibrato ? { tabVibrato: true } : {}),
            ...(tabString ? { tabString } : {}),
            ...(tabFret !== undefined && !isNaN(tabFret) ? { tabFret } : {})
        });
        pendingOttavaStart = null;
        pendingPedalStart = false;
    });

    return {
        notes,
        title,
        key,
        timeSignature,
        tempo,
        keyChanges,
        timeChanges,
        tempoChanges,
        tempoRamps
    };
}

function escapeXml(unsafe) {
    return (unsafe || '').replace(/[<>&'"]/g, function (c) {
        switch (c) {
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '&': return '&amp;';
            case '\'': return '&apos;';
            case '"': return '&quot;';
        }
    });
}

